/**
 * Which query params a dashboard tile receives. Pure so the rules can be tested
 * without rendering the view.
 *
 * `accepted` is what the tile's query takes, from the catalogue of the tile's
 * OWN source; `null` means the catalogue cannot say (composite charts), in
 * which case nothing is withheld.
 */

export type Accepted = string[] | null;

/** The tile's query takes a time range, or we cannot tell. */
export const canTime = (accepted: Accepted): boolean =>
  accepted === null || (accepted.includes('from') && accepted.includes('to'));

interface Input {
  /** The dashboard has explicit filter settings (not a pre-settings dashboard). */
  configured: boolean;
  /** What the platform put on the embed URL. */
  embed: Record<string, string>;
  /** What the viewer chose in the filter bar. */
  selection: Record<string, string>;
  accepted: Accepted;
  /** The time window, empty when the time frame is off or unusable. */
  range: { from?: string; to?: string };
}

/**
 * A configured dashboard sends a tile only the filters its query accepts — an
 * unrecognised `from`/`to` could be ignored or rejected, and neither is a result
 * worth drawing. A dashboard saved before settings existed sends everything, as
 * it always did.
 */
export const tileParams = ({ configured, embed, selection, accepted, range }: Input) => {
  if (!configured) return { ...embed, ...selection };
  const chosen = Object.fromEntries(
    Object.entries(selection).filter(([k]) => accepted === null || accepted.includes(k)),
  );
  return { ...embed, ...chosen, ...(canTime(accepted) ? range : {}) };
};
